import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {useMessage} from "../../hooks/message"
import {Container} from "../../components/Container"
import {useSetSelector} from "../../hooks/set-selector"
import {backButtonToggle} from "../../store/ui/actions"
import {useLocalization} from "../../hooks/localization"
import {Row} from "../../components/Row"
import {ItemList} from "../../components/ItemList"
import {setGroupControlsAction} from "../../store/control/actions"
import {useKeyboard} from "../../hooks/keyboard"
import {SettingSelectComponent} from "../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../components/Select"
import {BridgeDeviceModel} from "../../types/bridge-device"
import {ControlTypeModel} from "../../types/control"
import {useFilterSelector} from "../../hooks/filter-selector"
import {AreaModel} from "../../store/control/state"
import cancelIcon from "../../assets/images/icons/alarm/cancel.png"
import "./index.scss"

export const RadioButton = memo(function RadioButton({
    isSelected = false,
    title,
    onClick
}: {
    isSelected: boolean
    title: string
    onClick: () => void
}) {
    return (
        <div className="radio-button-container" onClick={onClick}>
            <div className={`radio-pointer ${isSelected ? "radio-selected" : ""}`} />
            <span>{title}</span>
        </div>
    )
})

export const AllControlsScreen = memo(function AllControlsScreen() {
    const {t} = useLocalization()
    const [setActiveInput, renderKeyboard] = useKeyboard()
    const [search, setSearch] = useState<string>("")
    const dispatch = useDispatch()
    const searchRef = useRef<HTMLInputElement>()
    const {failure} = useMessage()

    const controlList = useSelector(state => state.control.controlList)
    const controlTypeList = useSelector(state => state.control.controlTypeList)
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList)
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList)

    const areaList = useFilterSelector(
        state => state.control.areaList,
        area => area.type === "room" || area.type === "place",
        []
    )
    const [openModal, setOpenModal] = useState(false)
    const [areaOption, setAreaOption] = useState<SelectItem<AreaModel>>(null)
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(null)
    const [controlType, setControlType] = useState<SelectItem<Omit<ControlTypeModel, "image_set">>>(null)

    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMemo(
        () =>
            bridgeDeviceList
                .filter(bridgeDevice => (bridgeDeviceTypeSet[bridgeDevice.type_id]?.output_count || 0) > 0)
                .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
                .map(bridgeDevice => ({
                    value: bridgeDevice,
                    label: bridgeDevice.name
                })),
        [bridgeDeviceList, bridgeDeviceTypeSet]
    )

    const controlTypeOptionList: SelectItem<Omit<ControlTypeModel, "image_set">>[] = useMemo(
        () =>
            controlTypeList
                .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
                .map(controlType => ({
                    value: controlType,
                    label: controlType.name
                })),
        [controlTypeList]
    )

    const areaOptionList: SelectItem<AreaModel>[] = useMemo(
        () => areaList.map(area => ({value: area, label: area.name})),
        [areaList]
    )

    const onBlur = useCallback(() => setActiveInput(null), [setActiveInput])
    const onFocus = useCallback(() => setActiveInput([searchRef, search, setSearch]), [setActiveInput, search])

    const [alphabeticalOrder, setAlphabeticalOrder] = useState(false)
    const [groupOrder, setGroupOrder] = useState(true)

    const onClickAlphabeticalOrder = useCallback(() => {
        setGroupOrder(false)
        setAlphabeticalOrder(true)
    }, [])

    const onClickGroupOrder = useCallback(() => {
        setAlphabeticalOrder(false)
        setGroupOrder(true)
    }, [])

    const [sortByName, setSortByName] = useState(true)
    const [sortByGroup, setSortByGroup] = useState(false)

    const onClickSort = useCallback(() => {
        if (alphabeticalOrder) {
            setSortByGroup(false)
            setSortByName(p => !p)
        } else if (groupOrder) {
            setSortByName(false)
            setSortByGroup(p => !p)
        }
    }, [alphabeticalOrder, groupOrder])
    const [advancedFilteredList, setAdvancedFilteredList] = useState(false)

    const searchControl = useMemo(() => {
        const newValue = controlList.filter(value => {
            if (advancedFilteredList) {
                if (
                    areaOption?.value != null &&
                    bridgeDevice?.value == null &&
                    controlType?.value == null &&
                    value?.area_id === areaOption?.value?.id
                ) {
                    return value
                } else if (
                    bridgeDevice?.value != null &&
                    controlType?.value == null &&
                    areaOption?.value == null &&
                    value?.bridge_device_id === bridgeDevice?.value?.id
                ) {
                    return value
                } else if (
                    controlType?.value != null &&
                    areaOption?.value == null &&
                    bridgeDevice?.value == null &&
                    value?.type_id === controlType?.value?.id
                ) {
                    return value
                } else if (
                    areaOption?.value != null &&
                    bridgeDevice?.value != null &&
                    controlType?.value == null &&
                    value?.area_id === areaOption?.value?.id &&
                    value?.bridge_device_id === bridgeDevice?.value?.id
                ) {
                    return value
                } else if (
                    areaOption?.value != null &&
                    bridgeDevice?.value == null &&
                    controlType?.value != null &&
                    value?.area_id === areaOption?.value?.id &&
                    value?.type_id === controlType?.value?.id
                ) {
                    return value
                } else if (
                    areaOption?.value == null &&
                    bridgeDevice?.value != null &&
                    controlType?.value != null &&
                    value?.bridge_device_id === bridgeDevice?.value?.id &&
                    value?.type_id === controlType?.value?.id
                ) {
                    return value
                } else if (
                    areaOption?.value != null &&
                    bridgeDevice?.value != null &&
                    controlType?.value != null &&
                    value?.area_id === areaOption?.value?.id &&
                    value?.bridge_device_id === bridgeDevice?.value?.id &&
                    value?.type_id === controlType?.value?.id
                ) {
                    return value
                }
                return null
            }
            return null
        })
        const t = advancedFilteredList ? [...newValue] : [...controlList]
        return t
            .filter(value => [value].find(r => r.name.toLowerCase().includes(search.toLowerCase())))
            .sort((a, b) =>
                alphabeticalOrder
                    ? sortByName
                        ? a.name > b.name
                            ? -1
                            : 1
                        : a.name > b.name
                        ? 1
                        : -1
                    : groupOrder && (sortByGroup ? (a.type_id > b.type_id ? -1 : 1) : a.type_id > b.type_id ? 1 : -1)
            )
    }, [
        search,
        controlList,
        sortByName,
        sortByGroup,
        alphabeticalOrder,
        groupOrder,
        advancedFilteredList,
        areaOption?.value,
        bridgeDevice?.value,
        controlType?.value
    ])

    const applyFilter = useCallback(() => setOpenModal(false), [])

    useEffect(() => {
        if (areaOption?.value == null && controlType?.value == null && bridgeDevice?.value == null) {
            setAdvancedFilteredList(false)
        } else {
            setAdvancedFilteredList(true)
        }
    }, [areaOption, bridgeDevice, controlType])

    //dispatch(setGroupControlsAction(controlList))
    dispatch(backButtonToggle({is_open: true, to: "/"}))
    return (
        <>
            <div className="functional-modal-backdrop" style={{display: openModal ? "flex" : "none"}}>
                <div className="fnl-modal-container">
                    <span>Gelişmiş Filtreleme</span>
                    <div className="fnl-modal-options">
                        <div className="fnl-option-field">
                            <span>Alan</span>
                            <div className="fnl-option">
                                <SettingSelectComponent
                                    label={""}
                                    options={areaOptionList}
                                    onChange={setAreaOption}
                                    value={areaOption}
                                />
                                <span onClick={() => setAreaOption(null)}>
                                    <img src={cancelIcon} alt="" />
                                </span>
                            </div>
                        </div>
                        <div className="fnl-option-field">
                            <span>Cihaz</span>
                            <div className="fnl-option">
                                <SettingSelectComponent
                                    label={""}
                                    options={bridgeDeviceOptionList}
                                    onChange={setBridgeDevice}
                                    value={bridgeDevice}
                                />
                                <span onClick={() => setBridgeDevice(null)}>
                                    <img src={cancelIcon} alt="" />
                                </span>
                            </div>
                        </div>
                        <div className="fnl-option-field">
                            <span>Kontrol</span>
                            <div className="fnl-option">
                                <SettingSelectComponent
                                    label={""}
                                    options={controlTypeOptionList}
                                    onChange={setControlType}
                                    value={controlType}
                                />
                                <span onClick={() => setControlType(null)}>
                                    <img src={cancelIcon} alt="" />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="fnl-modal-actions">
                        <span onClick={applyFilter}>Tamam</span>
                    </div>
                </div>
            </div>
            <Container>
                <div className="page-wrapper">
                    <div className="page-search-input-container">
                        <RadioButton title="Grup Sıralama" isSelected={groupOrder} onClick={onClickGroupOrder} />
                        <RadioButton
                            title="Alfabetik Sıralama"
                            isSelected={alphabeticalOrder}
                            onClick={onClickAlphabeticalOrder}
                        />
                        <span onClick={onClickSort}>Sıralamayı Değiştir</span>
                        <span onClick={() => setOpenModal(true)}>Gelişmiş Filtreleme</span>
                    </div>
                    <div className="page-search-input-container">
                        <input
                            type="text"
                            maxLength={50}
                            placeholder="Kontrol Ara"
                            ref={searchRef}
                            onChange={e => setSearch(e.target.value)}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            value={search}
                        />
                        <div className="filter-clear-button" onClick={() => setSearch("")}>
                            {t("clear")}
                        </div>
                    </div>
                    <Row>
                        {renderIf(searchControl.length > 0)(
                            () => (
                                <ItemList controls={searchControl} />
                            ),
                            () => (
                                <span className="search-message-text">Arama bulunamadı.</span>
                            )
                        )}
                    </Row>
                </div>
            </Container>
            {renderKeyboard()}
        </>
    )
})
